/**
 * Order_Service.ts
 * Orders + upsell (delegates to SK Catalog / Upsell via the adapter).
 */

import { apiClient } from '../../api/axiosInstance';
import { Hosp_Menu_Url, Hosp_Orders_Url, Hosp_Orders_Today_Url, Hosp_Order_Upsell_Url, Hosp_Order_Items_Url, Hosp_Order_Decline_Url } from '../../api/endpoints';
import { newIdempotencyKey } from '../../lib/idempotency';
import type { CreateOrderInput, UpsellInput, AddOrderItemInput, RemoveOrderItemInput, ChangeOrderItemQtyInput, DeclineOrderItemInput } from '../../schemas/orders/order';

export interface MenuItemDTO { id: string; name: string; price: number; sections?: string[]; category?: string }

export interface TodaysOrderDTO {
  id: string;
  table?: string;
  guests?: number;
  status: string;
  lines: { id?: string; description: string; quantity?: string; unit_price?: string }[];
  subtotal: string;
  tax_total: string;
  grand_total: string;
}

/** Today's real orders, straight from billing — see orders.service.ts's
 *  listTodaysOrders. Rehydration source for "Open orders" after a reload,
 *  independent of which floor tables are still currently seated. */
export const getTodaysOrders = async (): Promise<TodaysOrderDTO[]> => {
  const { data } = await apiClient.get<TodaysOrderDTO[]>(Hosp_Orders_Today_Url);
  return data;
};

/** The org's real active menu — SK Catalog items, via the adapter. */
export const getMenu = async (q?: string): Promise<MenuItemDTO[]> => {
  const { data } = await apiClient.get<MenuItemDTO[]>(Hosp_Menu_Url, { params: q ? { q } : undefined });
  return data;
};

export const createOrder = async (input: CreateOrderInput): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Orders_Url, {
    ...input,
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey('order'),
  });
  return data;
};

export const fetchUpsell = async (orderId: string, input: UpsellInput): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Order_Upsell_Url(orderId), input);
  return data;
};

export const addOrderItem = async (orderId: string, input: AddOrderItemInput): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Order_Items_Url(orderId), {
    ...input,
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey('order-item'),
  });
  return data;
};

export const removeOrderItem = async (orderId: string, input: RemoveOrderItemInput): Promise<any> => {
  const { data } = await apiClient.delete(Hosp_Order_Items_Url(orderId), {
    data: { ...input, idempotencyKey: input.idempotencyKey ?? newIdempotencyKey('order-item-remove') },
  });
  return data;
};

export const changeOrderItemQty = async (orderId: string, input: ChangeOrderItemQtyInput): Promise<any> => {
  const { data } = await apiClient.patch(Hosp_Order_Items_Url(orderId), {
    ...input,
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey('order-item-qty'),
  });
  return data;
};

export const declineOrderItem = async (orderId: string, input: DeclineOrderItemInput): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Order_Decline_Url(orderId), input);
  return data;
};
